import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { LABELS } from "@match/shared";
import { PageHeader } from "@/components/Layout";
import { DeleteForever } from "@/components/delete";
import { AwaitPayoutPanel, PayoutReceivedPanel, payoutSessionOf } from "@/components/payout";
import { AttachBrokerPanel, CreateCycleAction, NewPropAccountPanel } from "@/components/prop-onboarding";
import { FaseVivaCard } from "@/pages/ClientDetail";
import { DD_APPROXIMATE_EXPLANATION, FeesPaid, HoldButton, HoldNote, OutcomePanel, outcomeSessionOf, PHASE_LABEL, Progress, RESET_LABEL, RemoveBrokerAction, ResetPanel, StateBadge } from "@/components/session";
import { AbandonPairButton, ClosePairButton, SessionTrade } from "@/components/trade";
import { RebalanceSection } from "@/components/rebalance";
import { AwaitingBrokerBNotice, BB_LEG_LABELS, HedgeRatioEditor, isBrokerPair, ratioText } from "@/components/broker-pair";
import { Button, Dash, ErrorNote, Field, MoreMenu, inputClass, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { dateTime, lots, money, percent, signClass } from "@/lib/format";
import { keys, useAccountGroups, useRefreshDirectory, useSession } from "@/lib/queries";
import { fractionToPct, pctIssue, pctToFraction } from "@/lib/units";
import type { HedgeLeg, SessionDetail as Detail } from "@/lib/types";

const TABS = [
  { key: "operazione", label: "Operazione" },
  { key: "risultato", label: "Risultato" },
  { key: "operazioni", label: "Operazioni" },
  { key: "parametri", label: "Parametri" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

export function SessionDetail() {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get("tab"))?.key ?? "operazione") as TabKey;
  const session = useSession(id);
  const canWrite = useCanWrite();
  const groups = useAccountGroups();
  const [outcome, setOutcome] = useState<"complete" | "fail" | null>(null);
  /** «Conto bruciato e reset»: la prop ha azzerato il conto, la Fase resta viva. */
  const [reset, setReset] = useState(false);
  /** I due gesti del payout: chiederlo e registrarlo che è arrivato. */
  const [payout, setPayout] = useState<"await" | "received" | null>(null);
  const [attachBroker, setAttachBroker] = useState(false);
  const [replaceBroker, setReplaceBroker] = useState(false);
  /** «Registra il conto» su una Fase senza ciclo che aspetta il conto prop della fase nuova. */
  const [newProp, setNewProp] = useState(false);

  if (session.isLoading) return <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>;
  if (session.error || !session.data) {
    return (
      <div className="px-6 py-6">
        <ErrorNote>{session.error ? errorMessage(session.error) : "Fase non trovata"}</ErrorNote>
        <Link to="/fasi" className="mt-3 inline-block text-sm text-muted hover:text-ink">
          Torna alle fasi
        </Link>
      </div>
    );
  }

  const s = session.data;
  const live = s.state === "ACTIVE" || s.state === "PAUSED";
  /*
   * In attesa di payout la Fase non opera — il tratto è chiuso — ma non è
   * finita: da qui si registra il payout arrivato, e si può ancora bruciare il
   * conto, che è un esito vero e può capitare mentre si aspetta un bonifico.
   * «Fase superata» no: in funded non esiste.
   */
  const awaiting = s.state === "AWAITING_PAYOUT";
  /** Il conto prop c'è, il broker no: nessuna apertura finché non arriva. */
  const awaitingBroker = s.state === "AWAITING_BROKER";
  const brokerAccounts = (groups.data?.find((g) => g.client.id === s.client.id)?.accounts ?? []).filter((a) => a.role === "BROKER");
  /*
   * SENZA CICLO la Fase opera senza contabilità. Il ciclo, quando c'è, si
   * restringe una volta sola qui: il payout e il resto della contabilità
   * ricevono un tipo che lo pretende, non un controllo da ricordarsi.
   */
  /*
   * La Fase viva com'e' nella scheda cliente: i conti del cliente che stanno in QUESTA Fase, dagli stessi dati
   * (`GET /accounts`, `liveSession`). Se il conto prop non c'è ancora in quell'elenco la scheda non si mostra.
   */
  const contiFase = (groups.data?.find((g) => g.client.id === s.client.id)?.accounts ?? []).filter((a) => a.liveSession?.id === s.id);
  const propFase = contiFase.find((a) => a.role === "PROP");
  const cardFase =
    propFase && propFase.liveSession
      ? { session: propFase.liveSession, prop: propFase, broker: contiFase.find((a) => a.role === "BROKER") }
      : null;
  const conCiclo = s.cycle ? (s as Detail & { cycle: NonNullable<Detail["cycle"]> }) : null;
  const programma = s.preset ?? s.cycle?.preset ?? null;
  /** Una Fase senza ciclo, chiusa, che aspetta il conto prop della fase nuova. */
  const aspettaProp = !s.cycle && s.awaitingPropPhase !== null;
  /*
   * BROKER CONTRO BROKER: conto A e conto B, il rapporto di copertura. Niente
   * esiti, payout, reset, ciclo, sostituzione o rimozione di B (il backend li
   * rifiuta, 409 `NOT_FOR_BROKER_PAIRS`): qui non compaiono. Si decide sulla
   * Fase, non sulla capacita': spenta la funzione, una BB aperta si chiude.
   */
  const bb = isBrokerPair(s);
  const simboli = `${s.symbols?.prop.brokerSymbol ?? s.canonicalSymbol} contro ${s.symbols?.broker.brokerSymbol ?? s.canonicalSymbol}`;

  return (
    <>
      <PageHeader
        title={bb ? `${s.client.name} · Broker contro broker` : `${s.client.name} · ${PHASE_LABEL(s.phase)}`}
        subtitle={
          bb
            ? `Predefinito: ${simboli} · rapporto ${ratioText(s.hedgeRatio)}`
            : `${programma ? `${programma.propFirm.name} ${programma.name}` : "Senza programma"}${s.cycle ? "" : " · senza contabilità"} · Predefinito: ${s.symbols?.prop.brokerSymbol ?? s.canonicalSymbol} contro ${s.symbols?.broker.brokerSymbol ?? s.canonicalSymbol}`
        }
        actions={
          <div className="flex items-center gap-2">
            <StateBadge kind="session" state={s.state} />
            {s.resetFrom && (
              <Link to={`/fasi/${s.resetFrom.id}`} className="text-xs text-muted hover:text-ink">
                Nata da reset · {PHASE_LABEL(s.resetFrom.phase)} del {dateTime(s.resetFrom.startedAt)}
              </Link>
            )}
            {/* Perche' e' ferma, accanto allo stato: chi arriva qui lo legge
                prima di chiedersi se riprenderla. */}
            {s.state === "PAUSED" && <HoldNote hold={{ reason: s.holdReason, note: s.holdNote, since: s.holdSince }} />}
            {canWrite && live && (
              <>
                <HoldButton session={s} />
                {!bb && (
                  <>
                    <Button onClick={() => setOutcome("complete")}>{LABELS.sessionState.COMPLETED}</Button>
                    <Button onClick={() => setOutcome("fail")}>{LABELS.sessionState.FAILED}</Button>
                    {s.brokerAccount && <Button onClick={() => setReset(true)}>{RESET_LABEL}</Button>}
                  </>
                )}
              </>
            )}
            {canWrite && live && !bb && s.phase === "FUNDED" && conCiclo && <Button onClick={() => setPayout("await")}>A payout</Button>}
            {/* Senza ciclo la voce c'è lo stesso: su Fase 2 e Funded è disabilitata, col motivo. */}
            {canWrite && (live || awaitingBroker) && !bb && !s.cycle && <CreateCycleAction session={s} variant="button" />}
            {canWrite && aspettaProp && (
              <Button variant="primary" onClick={() => setNewProp(true)}>
                Registra il conto prop
              </Button>
            )}
            {canWrite && awaiting && conCiclo && (
              <>
                <Button variant="primary" onClick={() => setPayout("received")}>
                  Payout arrivato
                </Button>
                <Button onClick={() => setOutcome("fail")}>{LABELS.sessionState.FAILED}</Button>
              </>
            )}
            {/*
              * Una Fase aperta per sbaglio si cancella, e solo se non ha
              * operazioni: il rifiuto lo dice. Qui non c'e' un'archiviazione
              * accanto — `HedgeSession.isArchived` non lo scrive nessuna rotta —
              * quindi non ci sono due meta' da mostrare insieme.
              */}
            {canWrite && (
              <DeleteForever kind="fase" id={s.id} label="questa Fase" size="sm" onDeleted={() => navigate("/fasi")} />
            )}
            {canWrite && live && !bb && s.brokerAccount && (
              <MoreMenu label="Altre azioni sulla fase">
                <button type="button" role="menuitem" onClick={() => setReplaceBroker(true)} className="block w-full rounded px-2 py-1.5 text-left text-sm text-ink hover:bg-raised">
                  Sostituisci conto broker
                </button>
                <RemoveBrokerAction sessionId={s.id} brokerLabel={s.brokerAccount.label} />
              </MoreMenu>
            )}
          </div>
        }
      />

      <nav className="flex gap-1 border-b border-line px-6" aria-label="Sezioni della fase">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setParams(t.key === "operazione" ? {} : { tab: t.key }, { replace: true })}
            aria-current={t.key === tab ? "page" : undefined}
            className={[
              "-mb-px border-b-2 px-3 py-2.5 text-sm transition-colors",
              t.key === tab ? "border-ink font-medium text-ink" : "border-transparent text-muted hover:text-ink",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="px-6 py-5">
        {/*
          * Lo stato si vede anche stando dentro la scheda, non solo di sfuggita
          * in cima: è il motivo per cui il pulsante «Apri operazione» non c'è.
          */}
        {awaiting && (
          <div className="mb-5 rounded-card border border-warn/40 bg-warn/5 px-4 py-3 text-sm">
            <p className="text-ink">{LABELS.sessionState.AWAITING_PAYOUT}</p>
            <p className="mt-1 text-muted">
              Il tratto di contabilità è chiuso sul saldo letto: finché il bonifico non è arrivato non si apre nessuna
              operazione. Quando arriva, «Payout arrivato» registra l'incasso, lo divide e fa ripartire il conto da una
              base nuova.
            </p>
          </div>
        )}
        {aspettaProp && s.awaitingPropPhase && (
          <div className="mb-5 rounded-card border border-loss/40 bg-loss/10 px-4 py-3 text-sm">
            <p className="text-ink">Aspetta il conto prop: {PHASE_LABEL(s.awaitingPropPhase)}</p>
            <p className="mt-1 text-muted">
              Questa fase è chiusa e opera senza contabilità. «Registra il conto prop» fa nascere la fase nuova — la stessa fase se il conto
              era bruciato — con programma, taglia e simbolo di questa. Il broker di prima è proposto, non riservato.
            </p>
          </div>
        )}
        {awaitingBroker && bb && (
          <div className="mb-5 max-w-xl">
            <AwaitingBrokerBNotice canWrite={canWrite} onAttach={() => setAttachBroker(true)} />
          </div>
        )}
        {awaitingBroker && !bb && cardFase && (
          // La stessa scheda di «Fasi attive» nella pagina cliente: l'attesa del broker si vede e si scioglie da qui.
          <div className="mb-5 max-w-xl">
            <FaseVivaCard
              entry={cardFase}
              cycle={s.cycle ? { id: s.cycle.id, label: s.cycle.label, propFirm: s.cycle.preset.propFirm, preset: s.cycle.preset } : null}
              highlighted={false}
              onHover={() => {}}
              onAttachBroker={() => setAttachBroker(true)}
              onSessionPage
            />
          </div>
        )}
        {tab === "operazione" && (awaitingBroker ? null : <SessionTrade s={s} canWrite={canWrite} />)}
        {tab === "risultato" && <ResultTab s={s} bb={bb} />}
        {/* I ribilanciamenti della Fase: solo con la contabilità, perché senza ciclo non c'è un netto. */}
        {tab === "risultato" && s.cycle && (
          <div className="mt-6 max-w-3xl">
            <RebalanceSection cycleId={s.cycle.id} sessionId={s.id} canWrite={canWrite} />
          </div>
        )}
        {tab === "operazioni" && <PairsTab s={s} canWrite={canWrite} bb={bb} />}
        {tab === "parametri" && (bb ? <BrokerPairParams s={s} canWrite={canWrite} /> : <ParamsTab s={s} canWrite={canWrite} onOutcome={setOutcome} onReset={() => setReset(true)} />)}
      </div>

      {outcome && (
        <OutcomePanel kind={outcome} session={outcomeSessionOf(s)} onClose={() => setOutcome(null)} />
      )}
      {reset && <ResetPanel session={outcomeSessionOf(s)} onClose={() => setReset(false)} />}

      {payout === "await" && conCiclo && <AwaitPayoutPanel session={payoutSessionOf(conCiclo)} onClose={() => setPayout(null)} />}
      {payout === "received" && conCiclo && <PayoutReceivedPanel session={payoutSessionOf(conCiclo)} onClose={() => setPayout(null)} />}
      {newProp && (
        <NewPropAccountPanel open clientId={s.client.id} clientName={s.client.name} brokerAccounts={brokerAccounts} defaultTargetSessionId={s.id} onClose={() => setNewProp(false)} />
      )}
      {attachBroker && (
        <AttachBrokerPanel
          open
          sessionId={s.id}
          clientId={s.client.id}
          clientName={s.client.name}
          brokerAccounts={brokerAccounts}
          onClose={() => setAttachBroker(false)}
        />
      )}
      {replaceBroker && (
        <AttachBrokerPanel
          open
          mode="replace"
          sessionId={s.id}
          clientId={s.client.id}
          clientName={s.client.name}
          brokerAccounts={brokerAccounts}
          onClose={() => setReplaceBroker(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Risultato
// ---------------------------------------------------------------------------

function ResultTab({ s, bb }: { s: Detail; bb: boolean }) {
  const sum = s.summary;
  const propCur = s.propAccount.currency;
  const brokerCur = s.brokerAccount?.currency ?? propCur;
  const sameCur = propCur === brokerCur;
  const cell = (n: number | null, cur: string) => (n === null ? <Dash title="Valute diverse: non si sommano senza cambio" /> : <span className={signClass(n)}>{money(n, cur)}</span>);
  /*
   * IL P&L DI OGGI ESISTE SOLO SE ESISTE UNA MEZZANOTTE.
   *
   * Senza il fuso del server prop letto dal gateway non c'e' un «oggi», e il
   * trattino qui non vuol dire «zero» ne' «valute diverse»: lo dice il titolo.
   * Il fuso si legge alla prima connessione del conto prop.
   */
  const dailyCell = (n: number | null, cur: string) =>
    !sum.dayBoundaryRead ? <Dash title="Fuso del server prop non ancora letto: senza mezzanotte non c'è un «oggi». Si legge alla prima connessione." /> : cell(n, cur);

  return (
    <div className="space-y-5">
      <div className="overflow-x-auto">
        <table className="w-full max-w-3xl text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs text-faint">
              <th className="py-2 pr-4 font-medium" />
              <th className="py-2 pr-4 text-right font-medium text-prop">{bb ? BB_LEG_LABELS.PROP : "Prop"}</th>
              <th className="py-2 pr-4 text-right font-medium text-broker">{bb ? BB_LEG_LABELS.BROKER : "Broker"}</th>
              <th className="py-2 text-right font-medium">Totale</th>
            </tr>
          </thead>
          <tbody>
            {/* Una riga per simbolo: lotti di strumenti diversi non si sommano, e nemmeno quelle delle due gambe. */}
            {sum.volumeBySymbol.length ? (
              sum.volumeBySymbol.map((v) => (
                <tr key={v.canonicalSymbol} className="border-b border-line/60">
                  <th className="py-2.5 pr-4 text-left font-normal text-muted">Volume · {v.canonicalSymbol}</th>
                  <td className="py-2.5 pr-4 text-right">{lots(v.prop)}</td>
                  <td className="py-2.5 pr-4 text-right">{lots(v.broker)}</td>
                  <td className="py-2.5 text-right">
                    <Dash title="I lotti delle due gambe non si sommano" />
                  </td>
                </tr>
              ))
            ) : (
              <tr className="border-b border-line/60">
                <th className="py-2.5 pr-4 text-left font-normal text-muted">Volume</th>
                <td className="py-2.5 pr-4 text-right">
                  <Dash title="Nessuna operazione eseguita" />
                </td>
                <td className="py-2.5 pr-4 text-right">
                  <Dash title="Nessuna operazione eseguita" />
                </td>
                <td className="py-2.5 text-right">
                  <Dash title="I lotti delle due gambe non si sommano" />
                </td>
              </tr>
            )}
            <tr className="border-b border-line/60">
              <th className="py-2.5 pr-4 text-left font-normal text-muted">P&L</th>
              <td className="py-2.5 pr-4 text-right">{cell(sum.pnl.prop, propCur)}</td>
              <td className="py-2.5 pr-4 text-right">{cell(sum.pnl.broker, brokerCur)}</td>
              <td className="py-2.5 text-right">{cell(sum.pnl.total, propCur)}</td>
            </tr>
            <tr className="border-b border-line/60">
              <th className="py-2.5 pr-4 text-left font-normal text-muted">P&L di oggi</th>
              <td className="py-2.5 pr-4 text-right">{dailyCell(sum.dailyPnl.prop, propCur)}</td>
              <td className="py-2.5 pr-4 text-right">{dailyCell(sum.dailyPnl.broker, brokerCur)}</td>
              <td className="py-2.5 text-right">{dailyCell(sum.dailyPnl.total, propCur)}</td>
            </tr>
          </tbody>
        </table>
        {!sameCur && (
          <p className="mt-2 text-xs text-faint">
            {bb ? `Broker A in ${propCur}, broker B in ${brokerCur}` : `Prop in ${propCur}, broker in ${brokerCur}`}: il totale richiede un cambio e non viene mostrato.
          </p>
        )}
      </div>

      {/* Broker contro broker: niente drawdown, target né recover. */}
      {!bb && (
      <div className="grid gap-4 md:grid-cols-2">
        <Metric title="DD residuo" value={`${s.ddUsedIsApproximate ? "≈ " : ""}${money(sum.ddRemaining, propCur)}`}>
          <Progress
            label="DD usato"
            tone="prop"
            approximate={s.ddUsedIsApproximate}
            ratio={sum.ddUsedRatio}
            detail={`${money(sum.ddUsed, propCur)} di ${money(sum.maxDdMoney, propCur)}`}
          />
          {s.ddUsedIsApproximate && (
            <p className="mt-1.5 text-xs text-faint">
              Base {s.maxDdBasis ? LABELS.ddBasis[s.maxDdBasis].toLowerCase() : "del DD"}: {DD_APPROXIMATE_EXPLANATION.charAt(0).toLowerCase() + DD_APPROXIMATE_EXPLANATION.slice(1)}
            </p>
          )}
        </Metric>
        <Metric title="DD giornaliero residuo" value={sum.dailyDdRemaining === null ? "—" : money(sum.dailyDdRemaining, propCur)}>
          <p className="text-xs text-faint">
            {sum.dailyDdMoney === null
              ? "La prop non prevede un DD giornaliero."
              : !sum.dayBoundaryRead
                ? `Limite ${money(sum.dailyDdMoney, propCur)} al giorno, ma il fuso del server prop non è ancora stato letto: senza mezzanotte non si può dire quanto ne resta oggi. Si legge alla prima connessione.`
                : `su ${money(sum.dailyDdMoney, propCur)} al giorno, fuso del server prop letto dal gateway`}
          </p>
        </Metric>
        <Metric title="Target residuo" value={sum.targetRemaining === null ? "—" : money(sum.targetRemaining, propCur)}>
          <Progress
            label="Target"
            ratio={sum.targetRatio}
            detail={sum.targetMoney === null ? "—" : `${percent(s.challengeTargetPct)} = ${money(sum.targetMoney, propCur)}`}
          />
        </Metric>
        {/* Il recover è nella valuta del ciclo (conto prop): P&L del reale convertito coppia per coppia al tasso congelato. */}
        {s.cycle && sum.recoverRemaining !== null && sum.recoverTarget !== null ? (
          <Metric title={LABELS.terms.recoverAmount} value={`${sum.costsRecoveredFxAssumed ? "≈ " : ""}${money(sum.recoverRemaining, s.cycle.currency)}`}>
            <Progress
              label={LABELS.terms.costsRecovered}
              tone="broker"
              approximate={sum.costsRecoveredFxAssumed}
              approximateTitle={`Una coppia chiusa senza tasso ${brokerCur}->${s.cycle.currency} è entrata a tasso 1: il numero si corregge quando la fase si chiude e congela il tasso.`}
              ratio={sum.costsRecoveredRatio}
              detail={`${money(sum.costsRecovered, s.cycle.currency)} di ${money(sum.recoverTarget, s.cycle.currency)}`}
            />
          </Metric>
        ) : (
          // Senza ciclo non c'è un «da recuperare»: si dice, non si mostra uno zero.
          <Metric title={LABELS.terms.recoverAmount} value="—">
            <p className="text-xs text-faint">Questa fase opera senza contabilità: non c'è un ciclo, quindi niente da recuperare. «Crea ciclo» la fa partire, da una Fase 1.</p>
          </Metric>
        )}
      </div>
      )}
    </div>
  );
}

function Metric({ title, value, children }: { title: string; value: string; children: ReactNode }) {
  return (
    <div className="card p-4">
      <p className="text-xs text-faint">{title}</p>
      <p className="mt-0.5 text-xl font-semibold text-ink">{value}</p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Operazioni
// ---------------------------------------------------------------------------

function PairsTab({ s, canWrite, bb }: { s: Detail; canWrite: boolean; bb: boolean }) {
  const a = bb ? "broker A" : "prop";
  const b = bb ? "broker B" : "broker";
  if (!s.pairs.length) {
    return <p className="text-sm text-muted">Nessuna operazione. Apri la prima dalla scheda Operazione: comparirà qui con lotti, orari e P&L delle due gambe.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <p className="mb-3 text-xs text-faint">P&L = variazione del saldo del conto: profitto, commissioni e swap.</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs text-faint">
            <th className="py-2 pr-4 font-medium">#</th>
            <th className="py-2 pr-4 font-medium">Simbolo</th>
            <th className="py-2 pr-4 font-medium">Stato</th>
            <th className="py-2 pr-4 font-medium">Direzione {a}</th>
            <th className="py-2 pr-4 text-right font-medium text-prop">Lotti {a}</th>
            <th className="py-2 pr-4 text-right font-medium text-broker">Lotti {b}</th>
            <th className="py-2 pr-4 font-medium">Aperta</th>
            <th className="py-2 pr-4 font-medium">Chiusa</th>
            <th className="py-2 pr-4 text-right font-medium text-prop">P&L {a}</th>
            <th className="py-2 pr-4 text-right font-medium text-broker">P&L {b}</th>
            <th className="py-2 pr-4 font-medium">Causa di chiusura</th>
            <th className="py-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          {s.pairs.map((p) => {
            const prop = p.legs.find((l) => l.accountRole === "PROP");
            const broker = p.legs.find((l) => l.accountRole === "BROKER");
            return (
              <tr key={p.id} className="border-b border-line/60 align-top">
                <td className="py-2.5 pr-4 text-muted">{p.seq}</td>
                <td className="py-2.5 pr-4">{p.canonicalSymbol}</td>
                <td className="py-2.5 pr-4">{LABELS.pairState[p.state]}</td>
                <td className="py-2.5 pr-4">{p.propDirection === "LONG" ? "Long" : "Short"}</td>
                <td className="py-2.5 pr-4 text-right">{lots(p.propLots)}</td>
                <td className="py-2.5 pr-4 text-right">{lots(p.brokerLots)}</td>
                <td className="py-2.5 pr-4 text-muted">{dateTime(p.openedAt)}</td>
                <td className="py-2.5 pr-4 text-muted">{dateTime(p.closedAt)}</td>
                <td className="py-2.5 pr-4 text-right">
                  <LegPnl leg={prop} />
                </td>
                <td className="py-2.5 pr-4 text-right">
                  <LegPnl leg={broker} />
                </td>
                <td className="py-2.5 pr-4 text-muted">{p.closeReason ? LABELS.closeReason[p.closeReason] : <Dash />}</td>
                <td className="py-2.5 text-right">
                  {canWrite && ["PENDING", "BOTH_OPEN", "ONE_LEG"].includes(p.state) && (
                    <span className="inline-flex flex-wrap items-center justify-end gap-2">
                      <ClosePairButton sessionId={s.id} pair={p} size="sm" />
                      <AbandonPairButton sessionId={s.id} pair={p} size="sm" />
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LegPnl({ leg }: { leg: HedgeLeg | undefined }) {
  if (!leg) return <Dash />;
  if (leg.pnlEffective === null) return <Dash title="Gamba non chiusa" />;

  return <span className={signClass(Number(leg.pnlEffective))}>{money(leg.pnlEffective, leg.currency)}</span>;
}

// ---------------------------------------------------------------------------
// Parametri
// ---------------------------------------------------------------------------

function ParamsTab({ s, canWrite, onOutcome, onReset }: { s: Detail; canWrite: boolean; onOutcome: (k: "complete" | "fail") => void; onReset: () => void }) {
  const refresh = useRefreshDirectory();
  const live = s.state === "ACTIVE" || s.state === "PAUSED";
  const editable = canWrite && live;
  const [dailyDd, setDailyDd] = useState(fractionToPct(s.dailyDdPct));
  const [target, setTarget] = useState(fractionToPct(s.challengeTargetPct));
  /**
   * La soglia dello SCARTO fra le gambe, in denaro e in valuta prop.
   *
   * Vuoto = il predefinito, e il campo lo dice: scriverci dentro il numero
   * predefinito lo trasformerebbe in una scelta fatta, e nessuno saprebbe più
   * distinguere «va bene così» da «l'ho deciso io».
   */
  const [drift, setDrift] = useState(s.levelsDriftMaxMoney === null ? "" : String(Number(s.levelsDriftMaxMoney)));
  /** Il moltiplicatore predefinito: vuoto finché la prima apertura non lo salva. */
  const [defaultMult, setDefaultMult] = useState(s.hedgeRatio === null ? "" : String(Number(s.hedgeRatio)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    // Un testo non capito NON diventa `null`: in una modifica cancellerebbe un valore che c'era.
    const capito = pctIssue("DD giornaliero", dailyDd) ?? pctIssue("Target", target);
    if (capito) return setError(capito);
    const body = {
      dailyDdPct: pctToFraction(dailyDd),
      challengeTargetPct: pctToFraction(target),
      // Vuoto = torna al predefinito. Denaro, non percentuale: non passa da
      // `pctToFraction`, che qui dividerebbe per cento una cifra in dollari.
      levelsDriftMaxMoney: drift.trim() ? drift.trim().replace(",", ".") : null,
      ...(defaultMult.trim() && { hedgeRatio: defaultMult.trim().replace(",", ".") }),
    };
    if (body.levelsDriftMaxMoney !== null && !(Number(body.levelsDriftMaxMoney) > 0)) {
      return setError("La soglia dello scarto è un importo sopra zero, per esempio 10");
    }
    if (!defaultMult.trim() && s.hedgeRatio !== null) return setError("Il moltiplicatore predefinito non si svuota: scrivi un valore sopra zero, fino a 10");
    if (body.hedgeRatio !== undefined && !(Number(body.hedgeRatio) > 0 && Number(body.hedgeRatio) <= 10)) {
      return setError("Il moltiplicatore predefinito è un numero sopra zero, fino a 10");
    }
    setBusy(true);
    try {
      await api.sessions.update(s.id, body as never);
      await refresh();
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const num = (label: string, value: string, set: (v: string) => void, suffix?: string, hint?: string) => (
    <Field label={label} hint={hint}>
      {(id) => (
        <div className="relative">
          <input id={id} inputMode="decimal" disabled={!editable} value={value} onChange={(e) => set(e.target.value)} className={`${inputClass} ${suffix ? "pr-10" : ""}`} />
          {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
        </div>
      )}
    </Field>
  );

  const c = s.cycle;
  // Accumulatori e recover del ciclo: valuta del ciclo, cioè del conto prop.
  const cur = s.cycle?.currency ?? s.propAccount.currency;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form onSubmit={save} className="space-y-4">
        <h2 className="text-sm font-medium text-ink">Limiti della fase</h2>
        <p className="text-sm text-muted">
          Lotto, moltiplicatore, stop loss e take profit si scelgono a ogni operazione, nella scheda Operazione: qui c'è solo il moltiplicatore predefinito.
        </p>
        {!live && <p className="text-sm text-muted">Fase chiusa: i limiti restano come riferimento e non si modificano più.</p>}
        <div className="grid grid-cols-2 gap-3">
          {num("DD giornaliero", dailyDd, setDailyDd, "%")}
          {num("Target di fase", target, setTarget, "%")}
          {num(
            "Scarto massimo fra le gambe",
            drift,
            setDrift,
            cur,
            `Oltre questo scoperto fra le due gambe non si apre più niente su questa fase. Vuoto = ${s.levelsDriftLimit} ${cur}, il predefinito. ` +
              "Uno scarto sulla gamba broker si converte in valuta prop prima del confronto.",
          )}
          {num(
            "Moltiplicatore predefinito",
            defaultMult,
            setDefaultMult,
            undefined,
            "Proposto a ogni apertura e cambiabile lì, solo per quell'operazione. Vuoto = lo salva la prima apertura.",
          )}
        </div>
        <ErrorNote>{error}</ErrorNote>
        {editable && (
          <div className="flex items-center gap-3">
            <Button variant="primary" type="submit" disabled={busy}>
              {busy ? "Salvataggio…" : "Salva i limiti"}
            </Button>
            {saved && <span className="text-sm text-gain">Salvati.</span>}
          </div>
        )}
      </form>

      <div className="space-y-4">
        <h2 className="text-sm font-medium text-ink">{c ? "Ciclo" : "Senza contabilità"}</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-card border border-line bg-surface p-4 text-sm">
          {c ? (
            <>
              <Stat label="Etichetta">{c.label ?? <Dash />}</Stat>
              <Stat label="Fase del ciclo">{PHASE_LABEL(c.currentPhase)}</Stat>
              <Stat label="Taglia">{money(c.accountSize, s.propAccount.currency)}</Stat>
              <Stat label="Max DD">{`${percent(c.maxDdPct)} · ${LABELS.ddBasis[c.maxDdBasis]}`}</Stat>
              <Stat label={LABELS.terms.feesPaid}>
                <FeesPaid value={c.feesPaid} currency={cur} info={s.feesPaidInfo ?? undefined} />
              </Stat>
              <Stat label={LABELS.terms.realRealizedPnl}>
                <span className={signClass(Number(c.realRealizedPnl))}>{money(c.realRealizedPnl, cur)}</span>
              </Stat>
              <Stat label={LABELS.terms.adjustments}>{money(c.adjustments, cur)}</Stat>
              <Stat label={`${LABELS.terms.recoverAmount} (ciclo)`}>{money(c.recoverAmount, cur)}</Stat>
              <Stat label="Recover all'avvio della fase">{s.profitTarget === null ? <Dash /> : money(s.profitTarget, cur)}</Stat>
              <Stat label="Reset del prop">
                {s.propResetCount === 0 ? (
                  "Nessuno"
                ) : (
                  <>
                    {s.propResetCount}
                    {s.propResetAt ? ` · l'ultimo il ${dateTime(s.propResetAt)}` : ""}
                  </>
                )}
              </Stat>
            </>
          ) : (
            <>
              <Stat label="Programma">{s.preset ? `${s.preset.propFirm.name} · ${s.preset.name}` : <Dash title="La fase non porta il programma: esegui backfill:session-spec" />}</Stat>
              <Stat label="Taglia">{s.accountSize === null ? <Dash /> : money(s.accountSize, s.propAccount.currency)}</Stat>
              <Stat label="Max DD">{s.maxDdPct === null || s.maxDdBasis === null ? <Dash /> : `${percent(s.maxDdPct)} · ${LABELS.ddBasis[s.maxDdBasis]}`}</Stat>
              <p className="col-span-2 text-xs text-faint">
                Nessun ciclo: niente fee, recover, periodi né payout. «Crea ciclo» dà una contabilità alla fase, da una Fase 1.
              </p>
            </>
          )}
          {s.lastOperation && (
            <Stat label="Ultima operazione">
              {`n. ${s.lastOperation.seq} · ${Number(s.lastOperation.propLots)} lotti · x${Number(s.lastOperation.brokerMultiplier)} · SL ${s.lastOperation.slPoints} pt`}
            </Stat>
          )}
        </dl>
        {canWrite && live && (
          <div className="flex flex-wrap gap-2">
            <HoldButton session={s} />
            <Button onClick={() => onOutcome("complete")}>{LABELS.sessionState.COMPLETED}</Button>
            <Button onClick={() => onOutcome("fail")}>{LABELS.sessionState.FAILED}</Button>
            {c && s.brokerAccount && <Button onClick={onReset}>{RESET_LABEL}</Button>}
          </div>
        )}
      </div>
      <SessionHistory sessionId={s.id} />
    </div>
  );
}

/**
 * I PARAMETRI DI UNA FASE BROKER CONTRO BROKER: i due conti e il rapporto di
 * copertura, modificabile. Niente limiti di rischio, ciclo, esiti o reset.
 */
function BrokerPairParams({ s, canWrite }: { s: Detail; canWrite: boolean }) {
  const live = s.state === "ACTIVE" || s.state === "PAUSED";
  const conto = (a: Detail["propAccount"] | null) => (a ? `${a.label} · ${a.mt5Login}` : "da aggiungere");
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <HedgeRatioEditor s={s} canWrite={canWrite} />
      <div className="space-y-4">
        <h2 className="text-sm font-medium text-ink">Broker contro broker</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-card border border-line bg-surface p-4 text-sm">
          <Stat label={BB_LEG_LABELS.PROP}>
            <span className="text-prop">{conto(s.propAccount)}</span>
          </Stat>
          <Stat label={BB_LEG_LABELS.BROKER}>
            <span className="text-broker">{conto(s.brokerAccount)}</span>
          </Stat>
          <Stat label="Rapporto di copertura">{ratioText(s.hedgeRatio)}</Stat>
          {s.lastOperation && (
            <Stat label="Ultima operazione">{`n. ${s.lastOperation.seq} · ${Number(s.lastOperation.propLots)} lotti · x${Number(s.lastOperation.brokerMultiplier)} · SL ${s.lastOperation.slPoints} pt`}</Stat>
          )}
          <p className="col-span-2 text-xs text-faint">Nessun ciclo, drawdown, target né proxy: le guardie di esecuzione sono quelle di sempre.</p>
        </dl>
        {canWrite && live && (
          <div className="flex flex-wrap gap-2">
            <HoldButton session={s} />
          </div>
        )}
      </div>
      <SessionHistory sessionId={s.id} />
    </div>
  );
}

/** Le azioni dello storico dette a parole; le altre restano col loro codice. */
const HISTORY_LABEL: Record<string, string> = {
  SESSION_CREATED: "Fase creata",
  SESSION_BROKER_REMOVED: "Conto broker rimosso",
  SESSION_BROKER_ATTACHED: "Conto broker agganciato",
  SESSION_PARAMS_UPDATED: "Parametri modificati",
  SESSION_DEFAULT_MULTIPLIER_SET: "Moltiplicatore predefinito salvato",
  SESSION_AWAITING_PAYOUT: "A payout",
  SESSION_PROP_RESET: RESET_LABEL,
};

/** Un'operazione singola su un conto della Fase: il conto, lo strumento e, alla chiusura, il P&L. */
function singleTradeText(row: { action: string; payload: unknown }): string | null {
  if (row.action !== "SINGLE_TRADE_OPENED" && row.action !== "SINGLE_TRADE_CLOSED") return null;
  const p = (row.payload ?? {}) as { role?: string; canonicalSymbol?: string; side?: string; lots?: string; pnlEffective?: string | null; currency?: string };
  const conto = p.role === "PROP" ? "prop" : p.role === "BROKER" ? "broker" : "";
  const cosa = `${p.canonicalSymbol ?? "?"} ${p.side === "SELL" ? "Sell" : "Buy"} ${p.lots ?? "?"} lotti sul conto ${conto}`.trim();
  if (row.action === "SINGLE_TRADE_OPENED") return `Operazione singola aperta: ${cosa}`;
  const pnl = p.pnlEffective == null ? "P&L non ancora letto" : `P&L ${money(p.pnlEffective, p.currency ?? "")}`;
  return `Operazione singola chiusa: ${cosa}, ${pnl}`;
}

function historyText(row: { action: string; payload: unknown }): ReactNode {
  if (row.action === "SESSION_BROKER_REPLACED") {
    const p = row.payload as { from?: { label?: string; mt5Login?: string }; to?: { label?: string; mt5Login?: string } } | null;
    const conto = (a?: { label?: string; mt5Login?: string }) => (a ? `${a.label ?? "?"}${a.mt5Login ? ` (${a.mt5Login})` : ""}` : "?");
    return `Broker sostituito: ${conto(p?.from)} → ${conto(p?.to)}`;
  }
  return singleTradeText(row) ?? HISTORY_LABEL[row.action] ?? <span className="machine-id">{row.action}</span>;
}

/** LO STORICO: le righe di audit della Fase, dalla piu' recente, con chi le ha fatte. */
function SessionHistory({ sessionId }: { sessionId: string }) {
  const history = useQuery({ queryKey: [...keys.sessions, sessionId, "history"], queryFn: () => api.sessions.history(sessionId) });
  return (
    <section className="mt-6 max-w-3xl">
      <h3 className="mb-2 text-sm font-medium text-ink">Storico</h3>
      {history.isLoading ? (
        <p className="text-sm text-muted">Caricamento…</p>
      ) : history.error ? (
        <ErrorNote>{errorMessage(history.error)}</ErrorNote>
      ) : !history.data?.length ? (
        <p className="text-sm text-muted">Nessuna azione registrata su questa fase.</p>
      ) : (
        <ul className="divide-y divide-line rounded border border-line text-sm">
          {history.data.map((r) => (
            <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-3 py-2">
              <span className="text-ink">{historyText(r)}</span>
              <span className="text-xs text-muted">
                {dateTime(r.createdAt)} · {r.user ? (r.user.fullName ?? r.user.email) : "sistema"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}
